import { useState, useEffect, useRef, useCallback } from 'react';
import VideoRenderer, { type VideoRendererProps } from './VideoRenderer';
import RoadSegSettings from './RoadSegSettings';
import RoiEditor from './RoiEditor';
import TripwireEditor from './TripwireEditor';
import RetailRegionEditor from './RetailRegionEditor';
import CalibrationEditor from './CalibrationEditor';
import PlacePicker from './PlacePicker';
import PtzControl from './PtzControl';
import PicamControl from './PicamControl';
import AiLayerMenu from './AiLayerMenu';
import FullscreenButton from './FullscreenButton';
import { useFullscreen } from './fullscreen';
import { Icon } from '../../icons';
import { privacyLabel, type LayerRow, type LayerId, type PrivacyMode } from './aiLayers';
import type { GeoFix } from './geolocate';
import { planStop, ONSTOP_TIMEOUT_MS, type RecorderState } from './captureRecorder';
import './privacyBadge.css';

interface VideoPanelProps extends VideoRendererProps {
  isFloating: boolean;
  onToggleFloat: () => void;
  onStopPhoneStream: () => void;
  onTerminateStream: () => void;
  /** The AI layer picker's data — assembled once in App.tsx and shared with the
   *  floating header, so only one poll runs however many headers are mounted. */
  aiLayerRows: LayerRow[];
  aiLayerActive: LayerId;
  aiLayerBusy: boolean;
  aiLayerError: string | null;
  onSelectAiLayer: (id: LayerId) => void;
  /** This feed's privacy mode and its writer — see AiLayerMenu. */
  privacyMode: PrivacyMode;
  onSelectPrivacy: (mode: PrivacyMode) => void;
  privacyBusy: boolean;
  privacyError: string | null;
  privacyUnavailable: string | null;
  /** Lifted to App.tsx: the floating header's menu opens this panel too. */
  roadSegSettingsOpen: boolean;
  onToggleRoadSegSettings: () => void;
  onSwitchRoadSegMode: () => void;
  roadSegEnabled: boolean;
  roadSegMode: 'blur' | 'overlay';
  geoMaskEnabled: boolean;
  geoMaskMode: 'blur' | 'outline';
  geoMaskAltSource: 'elevation' | 'height' | 'computed' | 'altitude';
  geoMaskYawOffset: number;
  geoMaskCameraLabel: string;
  onToggleGeoMask: () => void;
  onSwitchGeoMaskMode: () => void;
  onCycleGeoMaskAlt: () => void;
  onAdjustYawOffset: (delta: number) => void;
  geoMaskPitchOffset: number;
  geoMaskHeightM: number | null;
  geoMaskRefused: string | null;
  onAdjustPitchOffset: (delta: number) => void;
  onSetGeoMaskHeight: (heightM: number | null) => void;
  apiUrl: string;
  authHeaders: (json?: boolean) => HeadersInit;
  onCaptureEvent?: (msg: string, type: 'success' | 'error') => void;
  /** Hands the map a callback for its next click, or null to stand down. Calibration needs
   *  the operator to point at the ground, and the map lives in App.tsx. */
  onRequestGroundPick?: (handler: ((p: { lat: number; lng: number }) => void) | null) => void;
  /** A geolocated pixel, for App.tsx to preview on the map before anything is written. */
  onGeoFix?: (fix: Extract<GeoFix, { ok: true }>, label: string) => void;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function VideoPanel({
  isFloating,
  onToggleFloat,
  onStopPhoneStream,
  onTerminateStream,
  aiLayerRows,
  aiLayerActive,
  aiLayerBusy,
  aiLayerError,
  onSelectAiLayer,
  privacyMode,
  onSelectPrivacy,
  privacyBusy,
  privacyError,
  privacyUnavailable,
  roadSegSettingsOpen,
  onToggleRoadSegSettings,
  onSwitchRoadSegMode,
  roadSegEnabled,
  roadSegMode,
  geoMaskEnabled,
  geoMaskMode,
  geoMaskAltSource,
  geoMaskYawOffset,
  geoMaskCameraLabel,
  onToggleGeoMask,
  onSwitchGeoMaskMode,
  onCycleGeoMaskAlt,
  onAdjustYawOffset,
  geoMaskPitchOffset,
  geoMaskHeightM,
  geoMaskRefused,
  onAdjustPitchOffset,
  onSetGeoMaskHeight,
  activeSourceId,
  activeStreamSource,
  detectionEnabled,
  apiUrl,
  authHeaders,
  onCaptureEvent,
  onRequestGroundPick,
  onGeoFix,
  ...rendererProps
}: VideoPanelProps) {
  const [showRoiEditor, setShowRoiEditor] = useState(false);
  const [hasRoi, setHasRoi] = useState(false);
  const [showLineEditor, setShowLineEditor] = useState(false);
  const [showRegionEditor, setShowRegionEditor] = useState(false);
  const [lineCount, setLineCount] = useState(0);
  const [showCalibration, setShowCalibration] = useState(false);
  const [showPlacePicker, setShowPlacePicker] = useState(false);
  const [showPtz, setShowPtz] = useState(false);
  const [ptzSteerable, setPtzSteerable] = useState(false);
  const [ptzStale, setPtzStale] = useState(false);
  const [isCalibrated, setIsCalibrated] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  const [snapshotFlash, setSnapshotFlash] = useState(false);
  const recordingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStart = useRef<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // The video wrapper is what goes full screen: the renderer, its overlays and
  // the capture bar travel with it, and nothing inside is remounted.
  const fullscreen = useFullscreen(wrapperRef);
  const [picamBarSlot, setPicamBarSlot] = useState<HTMLDivElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const clientRecording = useRef(false);

  const isServerSource = activeStreamSource?.type === 'dummy' || activeStreamSource?.type === 'rtmp' || activeStreamSource?.type === 'demo';
  const isPhoneSource = activeSourceId.startsWith('phone-');
  const showCaptureButtons = !isPhoneSource;

  // The detection area is bound to the FEED server-side, so switching source switches area.
  // Re-ask on every selection change, or the AREA button would keep advertising the last
  // camera's state. getAuthHeaders is module-level in App.tsx, so it is a stable dependency.
  useEffect(() => {
    if (!isServerSource) { setHasRoi(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/drone/detection/roi`, { headers: authHeaders() });
        if (!res.ok) { if (!cancelled) setHasRoi(false); return; }
        const data = await res.json();
        if (!cancelled) setHasRoi(Boolean(data && data.roi));
      } catch {
        if (!cancelled) setHasRoi(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders, activeSourceId, isServerSource]);

  // Camera steering is bound to the FEED, exactly like the detection area above, so the
  // PTZ button must be re-asked for on every source change or it would advertise the
  // last camera's capability. One call answers both "is there a steerable camera here"
  // and "does this deployment run PTZ at all" — on production the router replies
  // steerable:false and the button never appears, rather than 404-ing when clicked.
  useEffect(() => {
    if (!isServerSource) { setPtzSteerable(false); setShowPtz(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/ptz/feed`, { headers: authHeaders() });
        if (!res.ok) { if (!cancelled) setPtzSteerable(false); return; }
        const data = await res.json();
        if (!cancelled) setPtzSteerable(Boolean(data && data.steerable));
      } catch {
        if (!cancelled) setPtzSteerable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders, activeSourceId, isServerSource]);

  // Closing the panel clears the staleness badge with it, or the button goes on warning
  // about a camera nobody is looking at any more.
  useEffect(() => { if (!showPtz) setPtzStale(false); }, [showPtz]);

  // Counting lines are bound to the FEED as well (tripwires.stream_key, migration 063), so
  // the same rule applies again. `processing_stream_key` names the camera; a feed with no
  // reader has none, and then there is nothing to count on.
  useEffect(() => {
    if (!isServerSource) { setLineCount(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const src = await fetch(`${apiUrl}/api/drone/video-source`, { headers: authHeaders() });
        if (!src.ok) { if (!cancelled) setLineCount(0); return; }
        const key = (await src.json()).processing_stream_key;
        if (!key) { if (!cancelled) setLineCount(0); return; }
        const res = await fetch(
          `${apiUrl}/api/analytics/gates?scope=group&stream_key=${encodeURIComponent(key)}`,
          { headers: authHeaders() });
        if (!res.ok) { if (!cancelled) setLineCount(0); return; }
        const rows = await res.json();
        if (!cancelled) setLineCount(Array.isArray(rows) ? rows.length : 0);
      } catch {
        if (!cancelled) setLineCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders, activeSourceId, isServerSource]);

  // Calibration is bound to the FEED server-side too, so the same rule applies: re-ask on
  // every selection change or the CALIBRATE button advertises the last camera's state.
  useEffect(() => {
    if (!isServerSource) { setIsCalibrated(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/camera-calibration`, { headers: authHeaders() });
        if (!res.ok) { if (!cancelled) setIsCalibrated(false); return; }
        const data = await res.json();
        if (!cancelled) setIsCalibrated(Boolean(data && data.calibration));
      } catch {
        if (!cancelled) setIsCalibrated(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders, activeSourceId, isServerSource]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const resp = await fetch(`${apiUrl}/api/captures/record/status`, { headers: authHeaders() });
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (data.recording) {
          setIsRecording(true);
          setRecordingElapsed(data.elapsed);
          recordingStart.current = Date.now() - data.elapsed * 1000;
          if (!recordingTimer.current) {
            recordingTimer.current = setInterval(() => {
              setRecordingElapsed((Date.now() - recordingStart.current) / 1000);
            }, 1000);
          }
        }
      } catch {}
    };
    check();
    return () => { cancelled = true; };
  }, [apiUrl, authHeaders]);

  useEffect(() => {
    return () => {
      if (recordingTimer.current) clearInterval(recordingTimer.current);
    };
  }, []);

  const getMediaElement = useCallback((): HTMLVideoElement | HTMLImageElement | null => {
    if (!wrapperRef.current) return null;
    const video = wrapperRef.current.querySelector('video');
    if (video) return video;
    const img = wrapperRef.current.querySelector('img.video-stream');
    return img as HTMLImageElement | null;
  }, []);

  const clientSideSnapshot = useCallback(async (): Promise<Blob | null> => {
    const el = getMediaElement();
    if (!el) return null;
    const canvas = document.createElement('canvas');
    if (el instanceof HTMLVideoElement) {
      if (!el.videoWidth) return null;
      canvas.width = el.videoWidth;
      canvas.height = el.videoHeight;
    } else {
      if (!el.naturalWidth) return null;
      canvas.width = el.naturalWidth;
      canvas.height = el.naturalHeight;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(el, 0, 0);
    return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', 0.95));
  }, [getMediaElement]);

  const uploadCapture = useCallback(async (blob: Blob, type: 'snapshot' | 'recording', duration?: number) => {
    const form = new FormData();
    const ext = type === 'snapshot' ? 'jpg' : blob.type.includes('mp4') ? 'mp4' : 'webm';
    form.append('file', blob, `${type}.${ext}`);
    form.append('capture_type', type);
    form.append('stream_source', activeStreamSource?.type || 'unknown');
    form.append('stream_name', activeStreamSource?.name || 'Live Feed');
    if (duration != null) form.append('duration', String(duration));

    const hdrs = authHeaders();
    const hdrObj: Record<string, string> = {};
    if (hdrs instanceof Headers) {
      hdrs.forEach((v, k) => { if (k.toLowerCase() !== 'content-type') hdrObj[k] = v; });
    } else if (Array.isArray(hdrs)) {
      hdrs.forEach(([k, v]) => { if (k.toLowerCase() !== 'content-type') hdrObj[k] = v; });
    } else {
      Object.entries(hdrs).forEach(([k, v]) => { if (k.toLowerCase() !== 'content-type') hdrObj[k] = v; });
    }

    const resp = await fetch(`${apiUrl}/api/captures/upload`, {
      method: 'POST',
      headers: hdrObj,
      body: form,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return resp.json();
  }, [apiUrl, authHeaders, activeStreamSource]);

  const takeSnapshot = useCallback(async () => {
    try {
      if (isServerSource) {
        const streamKey = activeStreamSource
          ? (activeStreamSource.type === 'rtmp' && activeStreamSource.url
            ? `rtmp:${activeStreamSource.url}`
            : activeStreamSource.type)
          : undefined;

        const resp = await fetch(`${apiUrl}/api/captures/snapshot`, {
          method: 'POST',
          headers: authHeaders(true),
          body: JSON.stringify({
            stream_key: streamKey,
            stream_name: activeStreamSource?.name || 'Live Feed',
          }),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || 'Snapshot failed');
        }
      } else {
        const blob = await clientSideSnapshot();
        if (!blob) throw new Error('Could not capture frame from video');
        await uploadCapture(blob, 'snapshot');
      }

      setSnapshotFlash(true);
      setTimeout(() => setSnapshotFlash(false), 300);
      onCaptureEvent?.('Snapshot saved', 'success');
    } catch (e: any) {
      onCaptureEvent?.(e.message || 'Snapshot failed', 'error');
    }
  }, [apiUrl, authHeaders, activeStreamSource, isServerSource, clientSideSnapshot, uploadCapture, onCaptureEvent]);

  const startClientRecording = useCallback(() => {
    const el = getMediaElement();
    if (!el || !(el instanceof HTMLVideoElement)) return false;
    let stream: MediaStream;
    try {
      const vid = el as any;
      stream = vid.captureStream ? vid.captureStream() : vid.mozCaptureStream();
    } catch {
      return false;
    }
    recordedChunksRef.current = [];
    const codecs = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4;codecs=h264',
      'video/mp4',
    ];
    const mimeType = codecs.find(c => MediaRecorder.isTypeSupported(c));
    if (!mimeType) return false;
    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };
    recorder.start(1000);
    mediaRecorderRef.current = recorder;
    clientRecording.current = true;
    return true;
  }, [getMediaElement]);

  const stopClientRecording = useCallback(async () => {
    const recorder = mediaRecorderRef.current;
    const buffered = recordedChunksRef.current.reduce((n, c) => n + c.size, 0);
    const plan = planStop(recorder?.state as RecorderState | undefined, buffered);

    // Whatever happens below, the recording is over as far as this panel is
    // concerned. Clearing here rather than in a success branch is what stops a
    // failed stop from leaving a live recorder nobody can reach.
    const finish = async (upload: boolean) => {
      const mime = recorder?.mimeType || 'video/webm';
      const chunks = recordedChunksRef.current;
      mediaRecorderRef.current = null;
      clientRecording.current = false;
      recordedChunksRef.current = [];
      if (!upload || chunks.length === 0) return;
      const elapsed = (Date.now() - recordingStart.current) / 1000;
      try {
        await uploadCapture(new Blob(chunks, { type: mime }), 'recording', elapsed);
        onCaptureEvent?.('Recording saved', 'success');
      } catch (e: any) {
        onCaptureEvent?.(e.message || 'Upload recording failed', 'error');
      }
    };

    if (!plan.stop) {
      // The recorder ended itself — its <video> was torn down under it. Upload
      // what we hold; calling .stop() here would throw InvalidStateError.
      if (plan.salvage) onCaptureEvent?.('Feed ended — saving what was recorded', 'error');
      await finish(plan.salvage);
      return;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      const done = async (upload: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        await finish(upload);
        resolve();
      };
      // `onstop` is not guaranteed to arrive. Without this the await below
      // never returns and the button stays in REC for the rest of the session.
      const watchdog = setTimeout(() => { void done(true); }, ONSTOP_TIMEOUT_MS);
      recorder!.onstop = () => { void done(true); };
      try {
        recorder!.stop();
      } catch {
        void done(true);
      }
    });
  }, [uploadCapture, onCaptureEvent]);

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      try {
        if (clientRecording.current) {
          await stopClientRecording();
        } else {
          const resp = await fetch(`${apiUrl}/api/captures/record/stop`, {
            method: 'POST',
            headers: authHeaders(),
          });
          if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || 'Stop failed');
          }
          onCaptureEvent?.('Recording saved', 'success');
        }
      } catch (e: any) {
        onCaptureEvent?.(e.message || 'Stop recording failed', 'error');
      } finally {
        // In `finally`, never after the await: a stop that fails — a recorder
        // that ended itself, a backend restarted out from under a server-side
        // recording (404) — must still return the button to idle. Leaving it
        // in REC is how STOP became a button that does nothing for ever.
        setIsRecording(false);
        setRecordingElapsed(0);
        if (recordingTimer.current) {
          clearInterval(recordingTimer.current);
          recordingTimer.current = null;
        }
      }
    } else {
      try {
        if (isServerSource) {
          const streamKey = activeStreamSource
            ? (activeStreamSource.type === 'rtmp' && activeStreamSource.url
              ? `rtmp:${activeStreamSource.url}`
              : activeStreamSource.type)
            : undefined;

          const resp = await fetch(`${apiUrl}/api/captures/record/start`, {
            method: 'POST',
            headers: authHeaders(true),
            body: JSON.stringify({
              stream_key: streamKey,
              stream_name: activeStreamSource?.name || 'Live Feed',
            }),
          });

          if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || 'Record failed');
          }
        } else {
          const ok = startClientRecording();
          if (!ok) throw new Error('Cannot record this stream type');
        }

        setIsRecording(true);
        recordingStart.current = Date.now();
        setRecordingElapsed(0);
        recordingTimer.current = setInterval(() => {
          setRecordingElapsed((Date.now() - recordingStart.current) / 1000);
        }, 1000);
        onCaptureEvent?.('Recording started', 'success');
      } catch (e: any) {
        onCaptureEvent?.(e.message || 'Start recording failed', 'error');
      }
    }
  }, [isRecording, apiUrl, authHeaders, activeStreamSource, isServerSource, startClientRecording, stopClientRecording, onCaptureEvent]);

  return (
    <div className={`video-panel ${isFloating ? 'video-panel-floating' : ''}`}>
      {!isFloating && (
        <div className="panel-header">
          <div className="panel-header-right">
            {/* Nothing selected — don't claim LIVE over the NO SIGNAL frame */}
            {activeSourceId && <span className="live-indicator"><span className="live-dot" aria-hidden="true" />Live</span>}
            {privacyMode !== 'picture' && (
              <span className={`privacy-badge ${privacyMode === 'blur' ? 'blur' : ''}`}>
                {privacyLabel(privacyMode)}
              </span>
            )}
            <AiLayerMenu
              rows={aiLayerRows}
              active={aiLayerActive}
              busy={aiLayerBusy}
              error={aiLayerError}
              onSelect={onSelectAiLayer}
              roadSegMode={roadSegMode}
              onSwitchRoadSegMode={onSwitchRoadSegMode}
              onOpenRoadSegSettings={onToggleRoadSegSettings}
              geoMaskEnabled={geoMaskEnabled}
              geoMaskMode={geoMaskMode}
              geoMaskAltSource={geoMaskAltSource}
              geoMaskYawOffset={geoMaskYawOffset}
              geoMaskCameraLabel={geoMaskCameraLabel}
              onToggleGeoMask={onToggleGeoMask}
              onSwitchGeoMaskMode={onSwitchGeoMaskMode}
              onCycleGeoMaskAlt={onCycleGeoMaskAlt}
              onAdjustYawOffset={onAdjustYawOffset}
              geoMaskPitchOffset={geoMaskPitchOffset}
              geoMaskHeightM={geoMaskHeightM}
              geoMaskRefused={geoMaskRefused}
              onAdjustPitchOffset={onAdjustPitchOffset}
              onSetGeoMaskHeight={onSetGeoMaskHeight}
              privacyMode={privacyMode}
              onSelectPrivacy={onSelectPrivacy}
              privacyBusy={privacyBusy}
              privacyError={privacyError}
              privacyUnavailable={privacyUnavailable}
            />
            {isServerSource && (
              <button
                className={`detection-toggle ${hasRoi ? 'active' : ''}`}
                onClick={() => { setShowLineEditor(false); setShowRegionEditor(false); setShowRoiEditor(v => !v); }}
                title="Restrict detection to an area you draw on the video"
              >
                {hasRoi ? 'Area on' : 'Area'}
              </button>
            )}
            {isServerSource && (
              <button
                className={`detection-toggle ${lineCount ? 'active' : ''}`}
                onClick={() => { setShowRoiEditor(false); setShowRegionEditor(false); setShowLineEditor(v => !v); }}
                title="Draw a line on the video and count people crossing it each way"
              >
                {lineCount ? `Lines ${lineCount}` : 'Line'}
              </button>
            )}
            {isServerSource && (
              <button
                className={`detection-toggle ${showRegionEditor ? 'active' : ''}`}
                onClick={() => { setShowRoiEditor(false); setShowLineEditor(false); setShowRegionEditor(v => !v); }}
                title="Draw queue, counter and display regions on the video, and see where people spend time"
              >
                Regions
              </button>
            )}
            {ptzSteerable && (
              <button
                className={`detection-toggle ${ptzStale ? 'is-warning' : showPtz ? 'active' : ''}`}
                onClick={() => {
                  setShowRoiEditor(false); setShowLineEditor(false); setShowRegionEditor(false);
                  setShowCalibration(false); setShowPlacePicker(false);
                  setShowPtz(v => !v);
                }}
                title={ptzStale
                  ? 'Steer this camera — it is off preset, so its detection area, lines and calibration are stale'
                  : 'Pan and tilt this camera, and recall a saved view'}
              >
                {ptzStale && <Icon name="warning" />}PTZ
              </button>
            )}
            {isServerSource && onRequestGroundPick && (
              <button
                className={`detection-toggle ${isCalibrated ? 'active' : ''}`}
                onClick={() => {
                  setShowPlacePicker(false); setShowRoiEditor(false); setShowLineEditor(false);
                  setShowRegionEditor(false); setShowCalibration(v => !v);
                }}
                title="Tell the map where this camera's picture lands on the ground"
              >
                {isCalibrated ? 'Calibrated' : 'Calibrate'}
              </button>
            )}
            {isServerSource && isCalibrated && onGeoFix && (
              <button
                className={`detection-toggle ${showPlacePicker ? 'active' : ''}`}
                onClick={() => { setShowCalibration(false); setShowPlacePicker(v => !v); }}
                title="Click an object in the video to place it on the map"
              >
                Place
              </button>
            )}
            {isPhoneSource && (
              <button
                className="detection-toggle is-danger"
                onClick={onStopPhoneStream}
                title="Stop phone stream"
              >
                Stop
              </button>
            )}
            {(activeStreamSource?.type === 'rtmp' || activeStreamSource?.type === 'demo') && (
              <button
                className="detection-toggle is-danger"
                onClick={onTerminateStream}
                title="Terminate stream — stops the server-side reader so it does not keep running in the background"
              >
                End stream
              </button>
            )}
            {fullscreen.supported && (
              <FullscreenButton
                className="detection-toggle"
                active={fullscreen.active}
                onToggle={fullscreen.toggle}
              />
            )}
            <button
              className="detection-toggle"
              onClick={onToggleFloat}
              title="Pop out to floating window"
              aria-label="Pop out to floating window"
            >
              <Icon name="expand" />
            </button>
          </div>
        </div>
      )}
      {roadSegSettingsOpen && roadSegEnabled && (
        <RoadSegSettings
          apiUrl={apiUrl}
          authHeaders={authHeaders}
          roadSegMode={roadSegMode}
        />
      )}
      <div ref={wrapperRef} className={`video-wrapper ${isFloating ? 'video-wrapper-floating' : ''}`} style={{ position: 'relative' }}>
        <VideoRenderer
          activeSourceId={activeSourceId}
          activeStreamSource={activeStreamSource}
          detectionEnabled={detectionEnabled}
          roadSegEnabled={roadSegEnabled}
          geoMaskEnabled={geoMaskEnabled}
          apiUrl={apiUrl}
          authHeaders={authHeaders}
          {...rendererProps}
        />
        {showRoiEditor && (
          <RoiEditor
            wrapperRef={wrapperRef}
            apiUrl={apiUrl}
            authHeaders={authHeaders}
            onClose={() => setShowRoiEditor(false)}
            onAreaChange={setHasRoi}
          />
        )}
        {showLineEditor && (
          <TripwireEditor
            wrapperRef={wrapperRef}
            apiUrl={apiUrl}
            authHeaders={authHeaders}
            onClose={() => setShowLineEditor(false)}
            onLinesChange={setLineCount}
          />
        )}
        {showRegionEditor && (
          <RetailRegionEditor
            wrapperRef={wrapperRef}
            apiUrl={apiUrl}
            authHeaders={authHeaders}
            onClose={() => setShowRegionEditor(false)}
          />
        )}
        {showCalibration && onRequestGroundPick && (
          <CalibrationEditor
            wrapperRef={wrapperRef}
            apiUrl={apiUrl}
            authHeaders={authHeaders}
            onClose={() => setShowCalibration(false)}
            onCalibrationChange={setIsCalibrated}
            onRequestGroundPick={onRequestGroundPick}
          />
        )}
        {showPtz && (
          <PtzControl
            wrapperRef={wrapperRef}
            apiUrl={apiUrl}
            authHeaders={authHeaders}
            onClose={() => setShowPtz(false)}
            onStaleChange={setPtzStale}
          />
        )}
        {activeStreamSource?.type === 'picam' && (
          /* The Pi-guided aircraft: box, CUT/ARM and the ground heartbeat. Mounted for as
             long as this panel shows that feed — unmounting it is what lets the Pi cut. */
          <PicamControl wrapperRef={wrapperRef} apiUrl={apiUrl} authHeaders={authHeaders}
                        barSlot={picamBarSlot} fullscreen={fullscreen.active} />
        )}
        {showPlacePicker && onGeoFix && (
          <PlacePicker
            wrapperRef={wrapperRef}
            apiUrl={apiUrl}
            authHeaders={authHeaders}
            onClose={() => setShowPlacePicker(false)}
            onFix={onGeoFix}
          />
        )}
        {snapshotFlash && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(255,255,255,0.6)',
            pointerEvents: 'none',
            animation: 'snapshot-flash 0.3s ease-out',
          }} />
        )}
        {isRecording && (
          <div className="video-rec-badge">
            <span className="video-rec-dot" aria-hidden="true" />
            <span>Rec <span className="num">{formatElapsed(recordingElapsed)}</span></span>
          </div>
        )}
        {(showCaptureButtons || fullscreen.supported) && (
          <div className="video-capture-bar">
            {showCaptureButtons && (<>
            <button
              className="video-capture-btn"
              onClick={takeSnapshot}
              title="Take snapshot"
              aria-label="Take snapshot"
            >
              <Icon name="snapshot" />
            </button>
            <button
              className={`video-capture-btn${isRecording ? ' is-recording' : ''}`}
              onClick={toggleRecording}
              title={isRecording ? 'Stop recording' : 'Start recording'}
              aria-label={isRecording ? 'Stop recording' : 'Start recording'}
            >
              {isRecording ? <span className="video-capture-stop" /> : <span className="video-capture-rec" />}
            </button>
            </>)}
            {/* On the picture, not only in the header: the floating window's
                header lives in App.tsx, and in full screen the header is not
                on screen at all, so this is also the way back out. */}
            {fullscreen.supported && (
              <FullscreenButton
                className="video-capture-btn"
                active={fullscreen.active}
                onToggle={fullscreen.toggle}
              />
            )}
          </div>
        )}
      </div>
      {activeStreamSource?.type === 'picam' && (
        /* PicamControl's bar renders here, under the picture rather than over it. */
        <div ref={setPicamBarSlot} className="picam-bar-slot" />
      )}
    </div>
  );
}
